/**
 * Customer Support tab landing — R9.x cluster.
 * AI Assistant opens inline (AssistantPanel); Live Chat + Alexa are stubs.
 * Atlas: Customer Support tab landing.
 */
"use client";

import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useSearchParams, useRouter } from "next/navigation";
import { Sparkles, MessageCircle, Mic, ArrowLeft } from "lucide-react";
import { SUPPORT_STUBS, formatStubStatus } from "@petci/shared-ui";
import { useT } from "@/lib/locale";

const AssistantPanel = dynamic(
  () => import("@/components/AssistantPanel").then((m) => m.AssistantPanel),
  { ssr: false },
);

export default function SupportPage() {
  const t = useT();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
  const [initialIntentId, setInitialIntentId] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const prompt = searchParams.get("prompt");
    if (prompt) {
      setInitialPrompt(prompt);
      // intentId (D4) accompanies insight deep-links so the fallback answer
      // resolves by id, not by matching the reworded prompt string.
      setInitialIntentId(searchParams.get("intentId"));
      setAssistantOpen(true);
      router.replace("/support", { scroll: false });
    }
  }, [searchParams, router]);

  if (assistantOpen) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setAssistantOpen(false)}
          className="inline-flex items-center gap-1 text-sm text-brand-primary-text hover:underline py-1 -my-1"
        >
          <ArrowLeft size={14} aria-hidden />
          {t("support.backToSupport")}
        </button>
        <AssistantPanel
          onClose={() => { setAssistantOpen(false); setInitialPrompt(null); setInitialIntentId(null); }}
          initialPrompt={initialPrompt ?? undefined}
          initialIntentId={initialIntentId ?? undefined}
          onPromptConsumed={() => { setInitialPrompt(null); setInitialIntentId(null); }}
        />
      </div>
    );
  }

  // Resolve the two SUPPORT stub cards' localized copy once (D1). Keys are
  // populated in the catalog; the English field is the fallback.
  const stubCards = SUPPORT_STUBS.map((s) => ({
    slug: s.slug,
    name: s.nameKey ? t(s.nameKey) : s.name,
    description: s.descriptionKey ? t(s.descriptionKey) : s.description,
    statusLabel: formatStubStatus(s.status, t).label,
  }));
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const liveChat = stubCards[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const voiceAlexa = stubCards[1]!;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-neutral-900">
          {t("support.title")}
        </h1>
        <p className="text-sm text-neutral-700 mt-1">
          {t("support.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* AI Assistant — opens inline */}
        <button
          onClick={() => setAssistantOpen(true)}
          className="rounded-xl border-2 border-brand-primary bg-brand-primary/5 p-6 shadow-sm hover:shadow-card-hover transition-shadow duration-base group flex flex-col text-left"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center">
              <Sparkles size={20} className="text-brand-primary-text" aria-hidden />
            </span>
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-semantic-success/10 text-semantic-success-text">
              {t("stubs.availableNow")}
            </span>
          </div>
          <h2 className="text-base font-semibold text-neutral-900 mb-2 group-hover:text-brand-primary-text transition-colors duration-fast">
            {t("support.aiTitle")}
          </h2>
          <p className="text-sm text-neutral-500 leading-relaxed flex-1">
            {t("support.aiDescription")}
          </p>
          <span className="mt-4 inline-flex text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
            {t("support.aiCta")}
          </span>
        </button>

        {/* Live Chat — stub */}
        <Link
          href="/support/live-chat"
          className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm hover:shadow-card-hover transition-shadow duration-base group flex flex-col"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-lg bg-brand-secondary/10 flex items-center justify-center">
              <MessageCircle size={20} className="text-brand-secondary" aria-hidden />
            </span>
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-brand-secondary/10 text-brand-secondary">
              {liveChat.statusLabel}
            </span>
          </div>
          <h2 className="text-base font-semibold text-neutral-900 mb-2 group-hover:text-brand-primary-text transition-colors duration-fast">
            {liveChat.name}
          </h2>
          <p className="text-sm text-neutral-500 leading-relaxed flex-1">
            {liveChat.description}
          </p>
          <span className="mt-4 inline-flex text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
            {t("stubs.viewDetails")}
          </span>
        </Link>

        {/* Voice/Alexa — stub */}
        <Link
          href="/support/voice-alexa"
          className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm hover:shadow-card-hover transition-shadow duration-base group flex flex-col"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center">
              <Mic size={20} className="text-brand-accent" aria-hidden />
            </span>
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-brand-secondary/10 text-brand-secondary">
              {voiceAlexa.statusLabel}
            </span>
          </div>
          <h2 className="text-base font-semibold text-neutral-900 mb-2 group-hover:text-brand-primary-text transition-colors duration-fast">
            {voiceAlexa.name}
          </h2>
          <p className="text-sm text-neutral-500 leading-relaxed flex-1">
            {voiceAlexa.description}
          </p>
          <span className="mt-4 inline-flex text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
            {t("stubs.viewDetails")}
          </span>
        </Link>
      </div>
    </div>
  );
}
